import React, { useCallback, useEffect, useState } from 'react';
import { EVAdoptionBarriersService, EVBarriersData } from '../../services/evAdoptionBarriersService';
import {
  ChartLayout,
  MethodologyContent,
  OverviewContent,
} from './ChartLayout';
import EVBarriersBarChart from './EVBarriersBarChart';
import EVBarriersScatterChart from './EVBarriersScatterChart';
import EVBarriersMetrics from './EVBarriersMetrics';
import { downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import {
  buildEvBarriersAuthoredBundle,
  buildEvBarriersKeyInsights,
  evBarriersOverviewDescription,
} from '../../domains/narrative/evBarriersNarrative';
import { AuthoredEquityPanel, AuthoredRelevancePanel } from '../../domains/narrative/narrativeSectionContent';
import { resolveMethodologyContentProps } from '../../domains/narrative/methodologyContentProps';

interface EVAdoptionBarriersChartProps {
  location: string;
}

const EVAdoptionBarriersChart: React.FC<EVAdoptionBarriersChartProps> = ({ location }) => {
  const [data, setData] = useState<EVBarriersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState('overview');
  const [chartType, setChartType] = useState<'bar' | 'scatter'>('scatter');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await EVAdoptionBarriersService.getData(location);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load EV adoption and income data');
      } finally {
        setLoading(false);
      }
    };

    if (location) {
      loadData();
    }
  }, [location]);

  const handleDownloadPNG = useCallback(() => {
    const canvas = document.querySelector('#ev-adoption-barriers-chart canvas') as HTMLCanvasElement;
    if (canvas) {
      const link = document.createElement('a');
      link.download = `${formatFilename(location, 'EV_Adoption_and_Income')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  }, [location]);

  const handleDownloadCSV = useCallback(() => {
    if (!data) return;
    const rows = data.quintiles.map((q: any) => ({
      quintile: q.quintile,
      income_min: q.incomeRange?.min ?? '',
      income_max: q.incomeRange?.max ?? '',
      tract_count: q.tractCount,
      avg_income: q.avgIncome,
      avg_ev_adoption: q.avgEVAdoption,
      total_population: q.totalPopulation,
      avg_education_pct: q.avgEducation,
      avg_homeownership_pct: q.avgHomeownership
    }));
    downloadDataAsCSV(rows, formatFilename(location, 'EV_Adoption_and_Income_Data'));
  }, [location, data]);

  if (loading) {
    return <div className="text-center p-8">Loading EV adoption and income data...</div>;
  }

  if (error) {
    return <div className="text-center p-8 text-red-600">Error: {error}</div>;
  }

  if (!data) {
    return <div className="text-center p-8">No data available</div>;
  }

  const authored = buildEvBarriersAuthoredBundle(location, data);

  const chartContent = (
    <div id="ev-adoption-barriers-chart">
      {chartType === 'bar' ? (
        <EVBarriersBarChart chartData={data.barChartData} chartOptions={data.barChartOptions} />
      ) : (
        <EVBarriersScatterChart
          chartData={data.scatterChartData}
          chartOptions={data.scatterChartOptions}
        />
      )}
    </div>
  );

  const keyMetrics = <EVBarriersMetrics metrics={data.metrics} quintiles={data.quintiles} />;

  return (
    <ChartLayout
      title={`EV Adoption and Income – ${data.county}`}
      description={`Tract-level association between median household income and plug-in vehicle adoption in ${data.county}.`}
      keyMetrics={keyMetrics}
      chartContent={chartContent}
      chartSelector={{
        options: [
          { value: 'scatter', label: 'Scatter Plot' },
          { value: 'bar', label: 'Bar Chart' }
        ],
        value: chartType,
        onChange: (value: string) => setChartType(value as 'bar' | 'scatter')
      }}
      narrativeViewId="income_quintile"
      overviewContent={
        <OverviewContent
          title="Overview"
          description={authored.overview || evBarriersOverviewDescription()}
        >
          <p>{authored.keyInsights || buildEvBarriersKeyInsights(location, data)}</p>
        </OverviewContent>
      }
      relevanceContent={
        <AuthoredRelevancePanel title="Relevance" bundle={authored} />
      }
      equityContent={
        <AuthoredEquityPanel title="Equity" bundle={authored} />
      }
      methodologyContent={
        (() => {
          const legacy = {
            title: 'Methodology',
            description: 'Descriptive tract-level snapshot within the analysis county.',
            dataSources: [
              'ev_by_tract_2023 (plug-in rate per 100 residents)',
              'ACS median household income (tract)',
            ],
            methodology: [
              'Income quintiles: equal tract counts per quintile within the county.',
              'Pearson correlation between tract income and EV rate (association, not causation).',
            ],
          };
          const props = resolveMethodologyContentProps(authored.methodology, legacy);
          return <MethodologyContent {...props} />;
        })()
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      layout="side-by-side"
      enableComments={true}
      location={location}
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={`EV_Adoption_and_Income_${location.replace(/\s+/g, '_')}`}
      csvFilename={`EV_Adoption_and_Income_Data_${location.replace(/\s+/g, '_')}`}
      registerPng={false}
    />
  );
};

export default EVAdoptionBarriersChart;
